import { ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import styled from "styled-components";
import { gsap } from "gsap";
import { useOnboarding } from "../../context/OnboardingContext";
import { ChatMessage } from "../../types";
import { generateId } from "../../utils/helpers";
import ReferralUtils from "../../utils/ReferralUtils";
import {
  Input,
  OptionButton,
  OptionsContainer,
} from "../styled/StyledComponents";
import ProgressBar from "./ProgressBar";

const testimonials = [
  {
    name: "Avery James",
    quote:
      "Maths used to scare me. Now I challenge myself every day. Thank you YoLearn!",
    grade: "Grade 10",
    nationality: "Canadian",
    stars: 5,
  },
  {
    name: "Harper Brooks",
    quote:
      "YoLearn has completely transformed my approach to science. Highly recommend!",
    grade: "Grade 12",
    nationality: "American",
    stars: 4,
  },
  {
    name: "Ella Morgan",
    quote:
      "My grades improved dramatically after using YoLearn. The interactive lessons are great!",
    grade: "University Student",
    nationality: "British",
    stars: 5,
  },
];

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #ffffff;
  width: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftSection = styled.div`
  flex: 0 0 60%;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  width: 100%;

  @media (max-width: 768px) {
    flex: 0 0 100%;
  }
`;

const RightSection = styled.div`
  flex: 0 0 40%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TestimonialContainer = styled.div`
  width: 437px;
  height: 485px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  position: relative;
  text-align: center;
  transition: transform 0.5s ease-in-out;

  @media (max-width: 1024px) {
    width: 380px;
    height: 420px;
  }
`;

const TestimonialImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const TestimonialContent = styled.div`
  position: absolute;
  bottom: 24px; /* Margin from bottom */
  left: 24px; /* Margin from left */
  right: 24px; /* Margin from right */
  z-index: 2;

  min-height: 120px;
  max-height: 160px;
  border-radius: 21.62px;
  padding: 20px;
  background: #00000066;
  box-shadow: 4px 6px 4px 0px #0000000d;
  backdrop-filter: blur(26.7px);

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box; /* Include padding in width/height calculations */
  color: white;
  text-align: left;
  overflow: hidden;

  p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.3;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  p:first-of-type {
    font-weight: 500;
    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 8px;
  }

  @media (max-width: 1024px) {
    min-height: 100px;
    max-height: 140px;
    padding: 16px;

    p {
      font-size: 0.85rem;
      line-height: 1.2;
    }
  }
`;

const TestimonialInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  flex-shrink: 0;

  div {
    /* Container for Grade and Canadian */
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
  }

  p {
    font-size: 0.75rem;
    opacity: 0.9;
    margin: 0;
    line-height: 0.9;
  }
  @media (max-width: 1024px) {
    p {
      font-size: 0.75rem;
    }
  }
`;

const StarRating = styled.div`
  display: flex;
  flex-direction: row !important;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2;
  flex-wrap: nowrap;
  gap: 2px;
  color: #ffd700;
  font-size: 0.9rem;

  span {
    display: inline-block;
    line-height: 1;
  }

  @media (max-width: 1024px) {
    font-size: 0.8rem;
  }
`;

const HeaderBar = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.8rem;
    flex-direction: column;
    gap: 0.5rem;

    div:last-child {
      font-size: 0.75rem !important;
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem 1rem 1rem 1rem;
  text-align: center;
  gap: 1.5rem;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;

  @keyframes slideInFromBottom {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem 0.8rem 1rem 0.8rem;
    gap: 1.2rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.5rem 1rem 0.5rem;
    gap: 1rem;
  }
`;

const QuestionContainer = styled.div<{ key: string }>`
  animation: slideInFromTop 0.5s ease-out;

  @keyframes slideInFromTop {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ChatMessageAnimation = styled.div`
  @keyframes slideInFromBottom {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FooterBar = styled.footer`
  width: 100%;
  padding: 1rem 2rem;
  display: flex;
  justify-content: flex-end;
  background: #ffffff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.03);

  @media (max-width: 480px) {
    padding: 0.8rem 1rem;
  }
`;

const ChatInteractionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  max-width: 800px;
  width: 100%;
  justify-content: flex-start;
  margin-left: 2rem;

  @media (max-width: 768px) {
    margin-left: 1rem;
    margin-right: 1rem;
    gap: 0.5rem;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const NextButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.9rem;
  background: transparent;
  border: 2px solid #419cda;
  border-radius: 12px;
  color: #419cda;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  min-width: 80px;

  &:hover {
    background: #419cda;
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.7rem;
    font-size: 0.9rem;
    min-width: 70px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.9rem;
  background: linear-gradient(
    135deg,
    transparent 0%,
    rgba(65, 156, 218, 0.05) 100%
  );
  border: 2px solid #419cda;
  border-radius: 12px;
  color: #419cda;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  min-width: 80px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    background: linear-gradient(135deg, #419cda 0%, #2563eb 100%);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(65, 156, 218, 0.2);
  }

  &:active {
    transform: translateY(0px) scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .back-text {
    @media (max-width: 480px) {
      display: none;
    }
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.7rem;
    font-size: 0.9rem;
    min-width: 50px;
  }
`;

const MascotImage = styled.img<{ $isSpinning: boolean }>`
  width: 90px;
  height: auto;
  flex-shrink: 0;
  animation: ${(props) =>
    props.$isSpinning ? "none" : "float 3s ease-in-out infinite"};
  cursor: pointer;
  transition: transform 0.2s ease;

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  &:hover {
    transform: translateY(-8px) scale(1.1);
    animation-play-state: paused;
  }

  @media (max-width: 768px) {
    width: 70px;
  }

  @media (max-width: 480px) {
    width: 60px;
  }
`;

const TailedMessageBubble = styled.div<{ $contentLength: number }>`
  position: relative;
  background: #ffffff;
  border: 2px solid #41ced3;
  border-radius: 16px;
  padding: 1.2rem 2rem;
  min-width: 200px;
  max-width: ${(props) => {
    if (props.$contentLength < 30) return "500px";
    if (props.$contentLength < 60) return "650px";
    return "600px";
  }};
  width: fit-content;
  animation: slideInFromLeft 0.5s ease-out;
  transition: transform 0.2s ease;

  @keyframes slideInFromLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &:hover {
    transform: translateY(-2px);
  }

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: -11px;
    width: 18px;
    height: 18px;
    background: #ffffff;
    border: 2px solid #41ced3;
    border-top: none;
    border-right: none;
    transform: translateY(-50%) rotate(45deg);
  }

  p {
    font-size: 1.1rem;
    font-weight: 500;
    margin: 0;
    color: #1f2937;
    line-height: 1.5;
  }

  @media (max-width: 768px) {
    max-width: 90vw;
    padding: 1rem 1.5rem;

    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    max-width: 85vw;
    padding: 0.8rem 1.2rem;
    min-width: 150px;

    &::before {
      display: none;
    }

    p {
      font-size: 0.95rem;
      line-height: 1.4;
    }
  }
`;

const ChatBot: React.FC = () => {
  const {
    currentStep,
    totalSteps,
    studentData,
    teacherData,
    updateStudentData,
    updateTeacherData,
    nextStep,
    setLoading,
    setUserType,
    prevStep,
    goToStep,
    goToStep0,
  } = useOnboarding();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentInput, setCurrentInput] = useState("");
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [inputType, setInputType] = useState<"text" | "select" | "multiselect">(
    "text"
  );
  const [showOthersInput, setShowOthersInput] = useState(false);
  const [othersText, setOthersText] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>([]);
  const [userName, setUserName] = useState("");
  const [currentUserType, setCurrentUserType] = useState<
    "student" | "teacher" | null
  >(null);
  const navigate = useNavigate();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  const mascotRef = useRef<HTMLImageElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 4000);

    return () => clearInterval(testimonialInterval);
  }, []);

  const currentTestimonial = testimonials[currentTestimonialIndex];

  const handleMascotClick = () => {
    if (mascotRef.current && !isSpinning) {
      setIsSpinning(true);
      gsap.to(mascotRef.current, {
        rotation: 360,
        duration: 0.8,
        ease: "power2.out",
        onComplete: () => {
          gsap.set(mascotRef.current, { rotation: 0 });
          setIsSpinning(false);
        },
      });
    }
  };

  const animateOptions = () => {
    if (optionsRef.current) {
      const buttons = optionsRef.current.querySelectorAll("button");
      gsap.fromTo(
        buttons,
        {
          opacity: 0,
          y: 30,
          scale: 0.8,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.4,
          stagger: 0.1,
          ease: "back.out(1.7)",
        }
      );
    }
  };
  useEffect(() => {
    if (
      inputType === "select" &&
      selectedOptions.length === 1 &&
      !selectedOptions.includes("Others (please specify)")
    ) {
      handleNext();
    }
  }, [selectedOptions]);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userTypeParam = urlParams.get("user_type");
    if (userTypeParam === "student" || userTypeParam === "teacher") {
      setCurrentUserType(userTypeParam);
    } else {
      const storedType = localStorage.getItem("userType");
      if (storedType === "student" || storedType === "teacher") {
        setCurrentUserType(storedType);
      }
    }
    if (!isInitializedRef.current && messages.length === 0) {
      initializeOnboarding();
      isInitializedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (currentUserType) {
      setUserType(currentUserType);
    }
  }, [currentUserType, setUserType]);

  const initializeOnboarding = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get("user_id");
    const userTypeParam = urlParams.get("user_type");

    if (
      userId &&
      userTypeParam &&
      (userTypeParam === "student" || userTypeParam === "teacher")
    ) {
      await startOnboardingFromParams(userId, userTypeParam);
    } else {
      addMessage(
        "Welcome to YoLearn! Please provide your user ID and select your type to continue with onboarding.",
        "question"
      );
      setInputType("text");
    }
  };

  const startOnboardingFromParams = async (
    userId: string,
    userTypeParam: "student" | "teacher"
  ) => {
    try {
      setLoading(true);
      const { authAPI } = await import("../../services/api");

      const result = await authAPI.startOnboarding(userId, userTypeParam);

      if (result.success && result.data) {
        const { user_id, user_type } = result.data;

        localStorage.setItem("userId", user_id);
        localStorage.setItem("userType", user_type);

        setCurrentUserType(userTypeParam);

        await checkOnboardingStatusAndInitialize(user_id, user_type);
      } else {
        addMessage(`Failed to start onboarding: ${result.error}`, "question");
      }
    } catch (error) {
      addMessage(
        "Sorry, there was an error starting your onboarding. Please try again.",
        "question"
      );
    } finally {
      setLoading(false);
    }
  };

  const checkOnboardingStatusAndInitialize = async (
    userId: string,
    userType: string
  ) => {
    try {
      const { onboardingAPI } = await import("../../services/api");
      const statusResult = await onboardingAPI.getOnboardingStatus();

      if (statusResult.success && statusResult.data?.data) {
        const { completed, has_data, profile } = statusResult.data.data;

        if (completed && profile) {
          setShowProfile(true);
          addMessage(
            `Welcome back! Your ${userType} profile is complete.`,
            "question"
          );
        } else if (has_data) {
          addMessage(
            `Welcome back! Let's continue your ${userType} onboarding.`,
            "question"
          );
          await initializeChat();
        } else {
          addMessage(`My name is...`, "question");
          setInputType("text");
        }
      } else {
        addMessage(`My name is...`, "question");
        setInputType("text");
      }
    } catch (error) {
      addMessage(
        `Hello! Welcome to YoLearn ${userType} onboarding. What should I call you?`,
        "question"
      );
      setInputType("text");
    }
  };

  useEffect(() => {
    if (!currentUserType) {
      const storedType = localStorage.getItem("userType");
      if (storedType === "student" || storedType === "teacher") {
        setCurrentUserType(storedType);
      }
    }
  }, [currentUserType]);

  useEffect(() => {
    scrollToMessagesBottom();
  }, [messages]);

  const scrollToMessagesBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const checkExistingOnboarding = async () => {
    try {
      const { onboardingAPI } = await import("../../services/api");
      const statusResult = await onboardingAPI.getOnboardingStatus();

      if (statusResult.success && statusResult.data?.data) {
        const { completed, profile } = statusResult.data.data;

        if (completed && profile) {
          setShowProfile(true);
          addMessage(`Welcome back! Here's your profile:`, "question");
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const saveCompleteOnboarding = async (data: any) => {
    try {
      const { onboardingAPI } = await import("../../services/api");

      const result = await onboardingAPI.saveComplete({
        ...data,
        completed: true,
      });

      if (result.success) {
        if (currentUserType === "student") {
          const accessToken = localStorage.getItem("yolearn_access_token");
          const refreshToken = localStorage.getItem("yolearn_refresh_token");
          const userId = localStorage.getItem("userId");

          let redirectUrl = "https://student.yolearn.ai";

          if (accessToken && userId) {
            redirectUrl += `?token=${accessToken}&user_id=${userId}`;
            if (refreshToken) {
              redirectUrl += `&refresh_token=${refreshToken}`;
            }
          }

          window.location.href = redirectUrl;
        } else {
          window.location.href = `https://app.yolearn.ai/${currentUserType}`;
        }
        return result;
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error: "Failed to save onboarding data" };
    }
  };

  const addMessage = (
    content: string,
    type: ChatMessage["type"] = "question",
    options?: string[],
    skipOthers?: boolean
  ) => {
    const message: ChatMessage = {
      id: generateId(),
      type,
      content,
      options: options
        ? skipOthers
          ? options
          : [...options, "Others (please specify)"]
        : undefined,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, message]);
  };
  const initializeChat = async () => {
    const hasCompletedOnboarding = await checkExistingOnboarding();

    if (!hasCompletedOnboarding) {
      addMessage(
        "Hello! Welcome to YoLearn. What should I call you?",
        "question"
      );
      setInputType("text");
    }
  };

  const handleNext = async () => {
    if (waitingForResponse) return;
    let response = "";

    const optionalSteps = {
      student: [6, 7, 8, 9],
      teacher: [5, 6, 7, 8],
    };

    const isOptionalStep =
      currentUserType && optionalSteps[currentUserType]?.includes(currentStep);

    if (inputType === "text" && currentInput.trim()) {
      if (currentStep === 1 && userName) {
        const matchedCountry = findMatchingCountry(currentInput.trim());
        if (!matchedCountry) {
          addMessage(
            "Please select a country from the dropdown list. Type to see available options.",
            "question"
          );
          setWaitingForResponse(false);
          setLoading(false);
          return;
        }
        addMessage(matchedCountry, "answer");
        response = matchedCountry;
      } else {
        addMessage(currentInput, "answer");
        response = currentInput.trim();
      }
    } else if (
      (inputType === "select" || inputType === "multiselect") &&
      selectedOptions.length > 0
    ) {
      let finalOptions = [...selectedOptions];
      if (
        selectedOptions.includes("Others (please specify)") &&
        othersText.trim()
      ) {
        finalOptions = finalOptions.filter(
          (o) => o !== "Others (please specify)"
        );
        finalOptions.push(othersText.trim());
      }
      const responseText = finalOptions.join(", ");
      addMessage(responseText, "answer");
      response = responseText;
    } else if (isOptionalStep) {
      addMessage("Skipped", "answer");
      response = "";
    }

    if (!response && !isOptionalStep) return;
    setWaitingForResponse(true);
    setLoading(true);
    try {
      if (!currentUserType) {
        addMessage(
          "Please start onboarding with valid user credentials first.",
          "question"
        );
        setWaitingForResponse(false);
        setLoading(false);
        return;
      }
      if (!userName) {
        setUserName(response);

        const userId = localStorage.getItem("userId");
        // Referral API call moved to dashboard after email verification. No referral call during onboarding.
        // if (userId) {
        //   const referralCode = ReferralUtils.getReferralCode();
        //   ReferralUtils.sendReferralData(
        //     referralCode || "",
        //     userId,
        //     currentUserType || "student"
        //   )
        //     .then((referralResult) => {
        //       if (referralResult.success) {
        //         if (referralCode) {
        //           ReferralUtils.clearReferralCode();
        //         }
        //       } else {
        //       }
        //     })
        //     .catch((referralError) => {});
        // }

        setWaitingForResponse(false);
        setLoading(false);
        addMessage(
          `Nice to meet you, ${response}! Which country are you ${
            currentUserType === "student" ? "studying" : "teaching"
          } in?`,
          "question"
        );
        setInputType("text");
        setCurrentInput("");
        return;
      }
      if (currentUserType === "student") {
        await handleStudentStep(response);
      } else {
        await handleTeacherStep(response);
      }
    } catch (error) {
      addMessage("I encountered an error. Please try again.", "question");
    } finally {
      setWaitingForResponse(false);
      setLoading(false);
      setCurrentInput("");
      setSelectedOptions([]);
      setShowOthersInput(false);
      setOthersText("");
    }
  };

  const handleStudentStep = async (response: string) => {
    const { studentAPI } = await import("../../services/api");
    toast.loading("Saving your response...", { id: "status" });
    switch (currentStep) {
      case 1:
        updateStudentData({ country: response });
        addMessage("Loading curriculum options...", "loading");
        try {
          const curricula = await studentAPI.getCurricula({
            country: response,
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "Which curriculum are you following?",
            "question",
            curricula.options
          );
          setInputType("select");
        } catch (error) {
          addMessage(
            "I couldn't fetch curriculum options. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 2:
        updateStudentData({ selected_curriculum: response });
        addMessage("Loading grade options...", "loading");
        try {
          const grades = await studentAPI.getGradesByCurriculum({
            curriculum: response,
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "What grade are you in?",
            "question",
            grades.options,
            true
          );
          setInputType("select");
        } catch (error) {
          addMessage(
            "I couldn't fetch grade options. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 3:
        updateStudentData({ grade: response });
        toast.success("Saved.", { id: "status" });
        addMessage("What is your age?", "question");
        setInputType("text");
        nextStep();
        break;
      case 4:
        updateStudentData({ age: response });
        addMessage("Loading subjects for your curriculum...", "loading");
        try {
          const subjects = await studentAPI.getSubjects({
            country: studentData.country || "",
            curriculum: studentData.selected_curriculum || "",
            grade: studentData.grade || "",
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "Which subjects are you studying? (You can select multiple)",
            "question",
            subjects.options
          );
          setInputType("multiselect");
        } catch (error) {
          addMessage(
            "I couldn't fetch subjects. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 5:
        const subjects = response.includes(",")
          ? response.split(", ")
          : [response];
        updateStudentData({ selected_subjects: subjects });
        addMessage("Getting learning interests for you...", "loading");
        try {
          const interests = await studentAPI.getLearningInterests({
            grade: studentData.grade || "",
            country: studentData.country || "",
            curriculum: studentData.selected_curriculum || "",
            subjects: subjects,
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "What are your learning interests? (Select multiple if you like)",
            "question",
            interests.options
          );
          setInputType("multiselect");
        } catch (error) {
          addMessage(
            "I couldn't fetch learning interests. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 6:
        const interests =
          response && response.includes(",")
            ? response.split(", ")
            : response
            ? [response]
            : [];
        updateStudentData({ selected_learning_interests: interests });
        addMessage("Finding learning styles that suit you...", "loading");
        try {
          const styles = await studentAPI.getLearningStyles({
            grade: studentData.grade || "",
            curriculum: studentData.selected_curriculum || "",
            country: studentData.country || "",
            subjects: studentData.selected_subjects || [],
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "What's your preferred learning style?",
            "question",
            styles.options
          );
          setInputType("select");
        } catch (error) {
          addMessage(
            "I couldn't fetch learning styles. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 7:
        updateStudentData({
          selected_learning_styles: response ? [response] : [],
        });
        addMessage("Getting help preference options...", "loading");
        try {
          const helpPrefs = await studentAPI.getHelpPreferences({
            grade: studentData.grade || "",
            curriculum: studentData.selected_curriculum || "",
            country: studentData.country || "",
            subjects: studentData.selected_subjects || [],
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "How do you prefer to get help when you're stuck?",
            "question",
            helpPrefs.options
          );
          setInputType("select");
        } catch (error) {
          addMessage(
            "I couldn't fetch help preferences. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 8:
        updateStudentData({
          selected_help_preferences: response ? [response] : [],
        });
        addMessage("Almost done! Getting learning goals...", "loading");
        try {
          const goals = await studentAPI.getLearningGoals({
            grade: studentData.grade || "",
            curriculum: studentData.selected_curriculum || "",
            country: studentData.country || "",
            subjects: studentData.selected_subjects || [],
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "What are your main learning goals? (Select multiple)",
            "question",
            goals.options
          );
          setInputType("multiselect");
        } catch (error) {
          addMessage(
            "I couldn't fetch learning goals. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 9:
        const goals =
          response && response.includes(",")
            ? response.split(", ")
            : response
            ? [response]
            : [];
        updateStudentData({ selected_learning_goals: goals });
        try {
          const completeData = {
            name: userName,
            ...studentData,
            selected_learning_goals: goals,
            completed: true,
          };

          const saveResult = await saveCompleteOnboarding(completeData);

          if (saveResult.success) {
            toast.success("Saved.", { id: "status" });
            addMessage(
              "🎉 Fantastic! Your student profile is complete and saved. Here's your personalized learning profile:",
              "question"
            );
            toast.dismiss();
            setShowProfile(true);
            setInputType("text");
            nextStep();
          } else {
            toast.error("Cannot save.", { id: "status" });
            addMessage(
              "❌ There was an error saving your profile. Please try again.",
              "question"
            );
            toast.dismiss();
          }
        } catch (error) {
          toast.error("Cannot save.", { id: "status" });
          addMessage(
            "❌ There was an error completing your onboarding. Please try again.",
            "question"
          );
          toast.dismiss();
        }
        break;
      default:
        break;
    }
  };

  const handleTeacherStep = async (response: string) => {
    toast.loading("Saving your response...", { id: "status" });
    const { teacherAPI } = await import("../../services/api");
    switch (currentStep) {
      case 1:
        updateTeacherData({ country: response });
        addMessage("Loading curriculum options...", "loading");
        try {
          const curricula = await teacherAPI.getCurricula({
            country: response,
            grade_levels: "",
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "Which curriculum do you follow?",
            "question",
            curricula.options
          );
          setInputType("select");
        } catch (error) {
          toast.error("Cannot save.", { id: "status" });
          addMessage(
            "I couldn't fetch curricula. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 2:
        updateTeacherData({ selected_curriculum: response });
        addMessage("Loading grade level options...", "loading");
        try {
          const gradeLevels = await teacherAPI.getGradeLevels({
            country: teacherData.country || "",
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "Which grade levels do you teach?",
            "question",
            gradeLevels.options
          );
          setInputType("select");
        } catch (error) {
          addMessage(
            "I couldn't fetch grade levels. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 3:
        updateTeacherData({ selected_grade_levels: response });
        addMessage("Getting subject options...", "loading");
        try {
          const subjects = await teacherAPI.getSubjects({
            country: teacherData.country || "",
            curriculum: teacherData.selected_curriculum || "",
            grade_levels: response,
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "Which subjects do you teach? (Select multiple)",
            "question",
            subjects.options
          );
          setInputType("multiselect");
        } catch (error) {
          addMessage(
            "I couldn't fetch subjects. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 4:
        const teacherSubjects = response.includes(",")
          ? response.split(", ")
          : [response];
        updateTeacherData({ selected_subjects: teacherSubjects });
        addMessage("Loading teaching goals...", "loading");
        try {
          const goals = await teacherAPI.getTeachingGoals({
            country: teacherData.country || "",
            curriculum: teacherData.selected_curriculum || "",
            grade_levels: teacherData.selected_grade_levels || "",
            subjects: teacherSubjects,
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "What are your main teaching goals? (Select multiple)",
            "question",
            goals.options
          );
          setInputType("multiselect");
        } catch (error) {
          addMessage(
            "I couldn't fetch teaching goals. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 5:
        const teachingGoals =
          response && response.includes(",")
            ? response.split(", ")
            : response
            ? [response]
            : [];
        updateTeacherData({ selected_teaching_goals: teachingGoals });
        toast.success("Saved.", { id: "status" });
        addMessage("Getting tech comfort options...", "loading");
        try {
          const techComfort = await teacherAPI.getTechComfort({
            country: teacherData.country || "",
            grade_levels: teacherData.selected_grade_levels || "",
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "How comfortable are you with technology?",
            "question",
            techComfort.options
          );
          setInputType("select");
        } catch (error) {
          addMessage(
            "I couldn't fetch tech comfort options. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 6:
        updateTeacherData({ selected_tech_comfort: response || "" });
        addMessage("Loading lesson plan preferences...", "loading");
        try {
          const lessonPlans = await teacherAPI.getLessonPlanPreferences({
            country: teacherData.country || "",
            curriculum: teacherData.selected_curriculum || "",
            subjects: teacherData.selected_subjects || [],
            tech_comfort: response,
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "What are your lesson plan preferences?",
            "question",
            lessonPlans.options
          );
          setInputType("select");
        } catch (error) {
          addMessage(
            "I couldn't fetch lesson plan preferences. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 7:
        updateTeacherData({ selected_lesson_plan: response || "" });
        addMessage("Getting device access options...", "loading");
        try {
          const deviceAccess = await teacherAPI.getDeviceAccess({
            country: teacherData.country || "",
            grade_levels: teacherData.selected_grade_levels || "",
          });
          toast.success("Saved.", { id: "status" });
          addMessage(
            "What kind of device access do your students have?",
            "question",
            deviceAccess.options
          );
          setInputType("select");
        } catch (error) {
          addMessage(
            "I couldn't fetch device access options. Please try again.",
            "question"
          );
        }
        nextStep();
        break;
      case 8:
        updateTeacherData({ selected_device_access: response || "" });
        try {
          const selected_subjects = Array.isArray(teacherData.selected_subjects)
            ? teacherData.selected_subjects
            : typeof teacherData.selected_subjects === "string" &&
              teacherData.selected_subjects
            ? (teacherData.selected_subjects as string)
                .split(",")
                .map((s: string) => s.trim())
            : [];
          const selected_teaching_goals = Array.isArray(
            teacherData.selected_teaching_goals
          )
            ? teacherData.selected_teaching_goals
            : typeof teacherData.selected_teaching_goals === "string" &&
              teacherData.selected_teaching_goals
            ? (teacherData.selected_teaching_goals as string)
                .split(",")
                .map((s: string) => s.trim())
            : [];
          const selected_grade_levels = Array.isArray(
            teacherData.selected_grade_levels
          )
            ? teacherData.selected_grade_levels.join(",")
            : typeof teacherData.selected_grade_levels === "string"
            ? teacherData.selected_grade_levels
            : "";
          const selected_device_access = Array.isArray(
            teacherData.selected_device_access
          )
            ? teacherData.selected_device_access.join(",")
            : teacherData.selected_device_access || response || "";

          const completeData = {
            user_id: localStorage.getItem("userId"),
            user_type: "teacher",
            name: userName,
            country: teacherData.country,
            selected_grade_levels,
            selected_curriculum: teacherData.selected_curriculum,
            selected_subjects,
            selected_teaching_goals,
            selected_tech_comfort: teacherData.selected_tech_comfort,
            selected_lesson_plan: teacherData.selected_lesson_plan,
            selected_device_access,
            completed: true,
          };

          const saveResult = await saveCompleteOnboarding(completeData);

          if (saveResult.success) {
            toast.success("Saved.", { id: "status" });
            addMessage(
              "🎉 Perfect! Your teacher profile is complete and saved. Here's your personalized teaching profile:",
              "question"
            );
            toast.dismiss();
            setShowProfile(true);
            setInputType("text");
            nextStep();
          } else {
            toast.error("Cannot save.", { id: "status" });
            addMessage(
              "❌ There was an error saving your profile. Please try again.",
              "question"
            );
            toast.dismiss();
          }
        } catch (error) {
          toast.error("Cannot save.", { id: "status" });
          addMessage(
            "❌ There was an error completing your onboarding. Please try again.",
            "question"
          );
          toast.dismiss();
        }
        break;
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      const previousStep = currentStep;
      prevStep();
      setMessages((prevMessages) => {
        const lastQuestionIndex = prevMessages
          .map((m) => m.type)
          .lastIndexOf("question");
        if (lastQuestionIndex > 0) {
          const lastAnswerIndex = prevMessages
            .map((m) => m.type)
            .lastIndexOf("answer", lastQuestionIndex - 1);
          if (lastAnswerIndex !== -1) {
            return prevMessages.slice(0, lastAnswerIndex);
          }
        }
        return prevMessages.slice(0, -2);
      });
      setSelectedOptions([]);
      setCurrentInput("");

      if (currentUserType === "student") {
        if (previousStep === 3) {
          setInputType("select");
        } else if (previousStep === 4) {
          setInputType("text");
        } else if (previousStep === 5) {
          setInputType("multiselect");
        } else if (previousStep === 6) {
          setInputType("multiselect");
        } else if (previousStep === 7) {
          setInputType("multiselect");
        } else if (previousStep === 8) {
          setInputType("select");
        } else if (previousStep === 9) {
          setInputType("select");
        } else if (previousStep === 10) {
          setInputType("multiselect");
        }
      } else if (currentUserType === "teacher") {
        if (previousStep === 3) {
          setInputType("select");
        } else if (previousStep === 4) {
          setInputType("multiselect");
        } else if (previousStep === 5) {
          setInputType("multiselect");
        } else if (previousStep === 6) {
          setInputType("select");
        } else if (previousStep === 7) {
          setInputType("select");
        } else if (previousStep === 8) {
          setInputType("select");
        } else if (previousStep === 9) {
          setInputType("select");
        }
      }
    }
  };
  const handleOptionSelect = (option: string) => {
    if (option === "Others (please specify)") {
      setShowOthersInput(true);
      if (inputType === "multiselect") {
        setSelectedOptions((prev) =>
          prev.includes(option)
            ? prev.filter((o) => o !== option)
            : [...prev.filter((o) => o !== option), option]
        );
      } else {
        setSelectedOptions([option]);
      }
    } else {
      setShowOthersInput(false);
      setOthersText("");
      if (inputType === "multiselect") {
        setSelectedOptions((prev) => {
          const filtered = prev.filter((o) => o !== "Others (please specify)");
          if (filtered.includes(option)) {
            return filtered.filter((o) => o !== option);
          } else {
            return [...filtered, option];
          }
        });
      } else {
        if (selectedOptions.includes(option)) return;
        if (selectedOptions[0] === option) return;
        setSelectedOptions([option]);
      }
    }
  };

  const handleCountryInputChange = (value: string) => {
    setCurrentInput(value);
    if (value.length > 0) {
      const filtered = countries.filter((c) =>
        c.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCountries(filtered);
      setShowCountryDropdown(true);
    } else {
      setFilteredCountries([]);
      setShowCountryDropdown(false);
    }
  };

  const handleCountrySelect = (country: string) => {
    setCurrentInput(country);
    setShowCountryDropdown(false);
    setFilteredCountries([]);
    handleNext();
  };

  const findMatchingCountry = (input: string): string | null => {
    const exactMatch = countries.find(
      (c) => c.toLowerCase() === input.toLowerCase()
    );
    if (exactMatch) return exactMatch;

    const matchingCountries = countries.filter((c) =>
      c.toLowerCase().includes(input.toLowerCase())
    );

    return matchingCountries.length > 0 ? matchingCountries[0] : null;
  };

  const canProceed = () => {
    const optionalSteps = {
      student: [6, 7, 8, 9],
      teacher: [5, 6, 7, 8],
    };

    const isOptionalStep =
      currentUserType && optionalSteps[currentUserType]?.includes(currentStep);

    if (isOptionalStep) {
      return true;
    }

    if (inputType === "text") {
      return currentInput.trim().length > 0;
    } else {
      const hasSelection = selectedOptions.length > 0;
      const needsOthersText =
        selectedOptions.includes("Others (please specify)") &&
        !othersText.trim();
      return hasSelection && !needsOthersText;
    }
  };

  const hasUserSelection = () => {
    if (inputType === "text") {
      return currentInput.trim().length > 0;
    } else {
      const hasSelection = selectedOptions.length > 0;
      const needsOthersText =
        selectedOptions.includes("Others (please specify)") &&
        !othersText.trim();
      return hasSelection && !needsOthersText;
    }
  };

  const shouldShowButton = () => {
    const optionalSteps = {
      student: [6, 7, 8, 9],
      teacher: [5, 6, 7, 8],
    };
    const isOptionalStep =
      currentUserType && optionalSteps[currentUserType]?.includes(currentStep);
    return canProceed() || isOptionalStep;
  };

  const getButtonText = () => {
    const optionalSteps = {
      student: [6, 7, 8, 9],
      teacher: [5, 6, 7, 8],
    };

    const isOptionalStep =
      currentUserType && optionalSteps[currentUserType]?.includes(currentStep);

    if (isOptionalStep) {
      return hasUserSelection() ? "Next" : "Skip";
    }

    return "Next";
  };

  const totalStepsForUser = currentUserType === "student" ? 10 : 9;
  const stepOffset = userName ? 0 : 0;
  const progress = ((currentStep + stepOffset) / totalStepsForUser) * 100;

  const currentQuestionMessage = messages
    .filter((m) => m.type === "question")
    .slice(-1)[0];

  if (!showProfile && currentQuestionMessage) {
    return (
      <PageLayout>
        <HeaderBar>
          <div style={{ fontWeight: 700, fontSize: "1.25rem" }}>
            <img
              src={"/logo.png"}
              style={{
                width: "150px",
              }}
            />
          </div>
          <div
            style={{
              fontSize: "0.875rem",
              color: "#717171",
              padding: "0.5rem",
            }}
          >
            Site language: English ▾
          </div>
        </HeaderBar>

        <ContentWrapper>
          <LeftSection>
            {currentStep >= 2 &&
              currentStep <= (currentUserType === "student" ? 9 : 8) && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 2rem",
                    gap: "8px",
                    marginBlock: "1rem",
                    width: "100%",
                  }}
                >
                  {currentStep > 2 && (
                    <BackButton
                      onClick={handleBack}
                      disabled={currentStep <= 2}
                    >
                      <ArrowLeft size={22} strokeWidth={2.5} />
                      Back
                    </BackButton>
                  )}
                  <ProgressBar
                    currentStep={currentStep - 1}
                    totalSteps={currentUserType === "student" ? 8 : 7}
                  />
                  {shouldShowButton() && (
                    <NextButton onClick={handleNext}>
                      {getButtonText()}
                      <ArrowLeft
                        size={22}
                        strokeWidth={2.5}
                        style={{ transform: "rotate(180deg)" }}
                      />
                    </NextButton>
                  )}
                </div>
              )}

            <MainContent style={{ paddingTop: "2rem" }}>
              <ChatInteractionContainer
                key={String(currentQuestionMessage?.id || currentStep)}
                style={{
                  animation: "slideInFromBottom 0.5s ease-out",
                  animationFillMode: "both",
                }}
              >
                <MascotImage
                  ref={mascotRef}
                  src={"/yo.png"}
                  alt="YoLearn Mascot"
                  onClick={handleMascotClick}
                  title="Click me for a surprise! 🎉"
                  $isSpinning={isSpinning}
                />
                <TailedMessageBubble
                  ref={bubbleRef}
                  $contentLength={currentQuestionMessage?.content?.length || 0}
                >
                  <p>{currentQuestionMessage?.content}</p>
                </TailedMessageBubble>
              </ChatInteractionContainer>

              {currentQuestionMessage && inputType === "text" ? (
                <div
                  style={{
                    width: "100%",
                    maxWidth: "420px",
                    position: "relative",
                  }}
                >
                  <Input
                    type={currentStep === 4 ? "number" : "text"}
                    min={currentStep === 4 ? "1" : undefined}
                    max={currentStep === 4 ? "99" : undefined}
                    value={currentInput}
                    onChange={(e) => {
                      if (currentStep === 1 && userName) {
                        handleCountryInputChange(e.target.value);
                      } else if (currentStep === 4) {
                        // Age input validation - only numbers up to 99
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (/^\d{1,2}$/.test(value) && parseInt(value) <= 99)
                        ) {
                          setCurrentInput(value);
                        }
                      } else {
                        setCurrentInput(e.target.value);
                      }
                    }}
                    placeholder={
                      currentStep === 1 && userName
                        ? "Type or select your country..."
                        : currentStep === 4
                        ? "Enter your age"
                        : "Enter answer"
                    }
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && canProceed()) {
                        handleNext();
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setShowCountryDropdown(false);
                      }, 150);
                    }}
                    disabled={waitingForResponse}
                  />
                  {currentStep === 1 &&
                    userName &&
                    showCountryDropdown &&
                    filteredCountries.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          backgroundColor: "white",
                          border: "1px solid #e1e5e9",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          maxHeight: "160px",
                          overflowY: "auto",
                          zIndex: 1000,
                          marginTop: "4px",
                        }}
                      >
                        {filteredCountries.slice(0, 4).map((country, index) => (
                          <div
                            key={index}
                            style={{
                              padding: "12px 16px",
                              cursor: "pointer",
                              userSelect: "none",
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              boxSizing: "border-box",
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleCountrySelect(country);
                            }}
                          >
                            <span
                              style={{ pointerEvents: "none", width: "100%" }}
                            >
                              {country}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ) : (
                <OptionsContainer
                  ref={optionsRef}
                  style={{
                    width: "100%",
                    maxWidth: "600px",
                    justifySelf: "flex-start",
                    animation: "slideInFromBottom 0.5s ease-out",
                    animationDelay: "0.3s",
                    animationFillMode: "both",
                  }}
                >
                  {currentQuestionMessage.options?.map((option, index) => (
                    <OptionButton
                      key={index}
                      $selected={selectedOptions.includes(option)}
                      onClick={() => handleOptionSelect(option)}
                    >
                      {option}
                    </OptionButton>
                  ))}

                  {showOthersInput && (
                    <Input
                      value={othersText}
                      onChange={(e) => setOthersText(e.target.value)}
                      placeholder="Please specify..."
                      style={{ marginTop: "0.5rem" }}
                    />
                  )}
                </OptionsContainer>
              )}
            </MainContent>
          </LeftSection>

          <RightSection>
            <TestimonialContainer>
              <TestimonialImage src="/image3.png" alt="Student Testimonial" />
              <TestimonialContent key={currentTestimonialIndex}>
                <p>"{currentTestimonial.quote}"</p>
                <TestimonialInfoRow>
                  <div>
                    <p>{currentTestimonial.name}</p>
                    <p>
                      {currentTestimonial.grade} •{" "}
                      {currentTestimonial.nationality}
                    </p>
                  </div>
                  <StarRating>
                    {[...Array(currentTestimonial.stars)].map((_, i) => (
                      <span key={i}>⭐</span>
                    ))}
                  </StarRating>
                </TestimonialInfoRow>
              </TestimonialContent>
            </TestimonialContainer>
          </RightSection>
        </ContentWrapper>

        <FooterBar>
          <div
            style={{
              display: "flex",
            }}
          >
            <button
              style={{
                backgroundColor: "#fff",
                border: "1px solid #00000033",
                borderTopLeftRadius: "0.6rem",
                borderBottomLeftRadius: "0.6rem",
                padding: "0.1rem 0.3rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: currentStep <= 2 ? "not-allowed" : "pointer",
                opacity: currentStep <= 2 ? 0.5 : 1,
              }}
              onClick={handleBack}
              disabled={currentStep <= 2}
            >
              <ChevronUp strokeWidth={2} size={18} />
            </button>
            <button
              onClick={handleNext}
              disabled={!canProceed() || waitingForResponse}
              style={{
                backgroundColor: "#fff",
                border: "1px solid #00000033",
                borderTopRightRadius: "0.6rem",
                borderBottomRightRadius: "0.6rem",
                padding: "0.1rem 0.3rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ChevronDown strokeWidth={2} size={18} />
            </button>
          </div>
        </FooterBar>
      </PageLayout>
    );
  }

  return <div>Your data is saved successfully.</div>;
};

const countries: string[] = [
  "India",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Kenya",
  "Nigeria",
  "South Africa",
  "Singapore",
  "Germany",
  "France",
  "Brazil",
  "China",
  "Japan",
  "Russia",
  "Mexico",
  "Italy",
  "Spain",
  "Turkey",
  "Indonesia",
  "Pakistan",
  "Bangladesh",
  "Philippines",
  "Vietnam",
  "Egypt",
  "Argentina",
  "Poland",
  "Netherlands",
  "Saudi Arabia",
  "Malaysia",
  "Thailand",
  "Sweden",
  "Switzerland",
  "Belgium",
  "Greece",
  "Portugal",
  "Czech Republic",
  "Hungary",
  "Romania",
  "Ukraine",
  "Chile",
  "Colombia",
  "Peru",
  "Venezuela",
  "Morocco",
  "Algeria",
  "Ethiopia",
  "Ghana",
  "Tanzania",
  "Uganda",
  "Sri Lanka",
  "Nepal",
  "New Zealand",
  "Ireland",
  "Denmark",
  "Finland",
  "Norway",
  "Austria",
  "Israel",
  "South Korea",
  "UAE",
  "Qatar",
  "Kuwait",
  "Oman",
  "Jordan",
  "Lebanon",
  "Iraq",
  "Iran",
  "Afghanistan",
  "Myanmar",
  "Cambodia",
  "Laos",
  "Mongolia",
  "Kazakhstan",
  "Uzbekistan",
  "Turkmenistan",
  "Georgia",
  "Armenia",
  "Azerbaijan",
  "Slovakia",
  "Slovenia",
  "Croatia",
  "Serbia",
  "Bulgaria",
  "Estonia",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Iceland",
  "Malta",
  "Cyprus",
  "Costa Rica",
  "Panama",
  "Uruguay",
  "Paraguay",
  "Bolivia",
  "Ecuador",
  "Guatemala",
  "Honduras",
  "El Salvador",
  "Nicaragua",
  "Jamaica",
  "Trinidad and Tobago",
  "Barbados",
  "Bahamas",
  "Fiji",
  "Papua New Guinea",
  "Zimbabwe",
  "Zambia",
  "Botswana",
  "Namibia",
  "Mozambique",
  "Angola",
  "Senegal",
  "Ivory Coast",
  "Cameroon",
  "Congo",
  "Sudan",
  "Libya",
  "Tunisia",
  "Syria",
  "Yemen",
  "Hong Kong",
];

export default ChatBot;
